import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { ContentDismissalService } from '@moment/analytics/ContentDismissalService.ts';
import {
  addBridgeEventListener,
  removeBridgeEventListener,
} from '@moment/bridge/client-bridge.ts';
import { BridgeEvent } from '@moment/bridge/client-bridge-defs.ts';
import { AnalyticsService } from '@moment/analytics/AnalyticsService.ts';

/**
 * Manages content dismissal tracking lifecycle.
 *
 * Duration starts on SCREEN_UNLOCK (bridge event) or when the page becomes
 * visible again after being backgrounded — whichever comes first.
 * Duration stops when the page is backgrounded, unmounted, or an external
 * link is clicked.
 */
export function useContentDismissalTracking(analyticsService: AnalyticsService) {
  const location = useLocation();
  const serviceRef = useRef(new ContentDismissalService(analyticsService));

  useEffect(() => {
    const service = serviceRef.current;
    const searchParams = new URLSearchParams(location.search);
    const partnerId = searchParams.get('partnerid') || '';

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        service.startTracking(partnerId, searchParams);
      } else if (document.visibilityState === 'hidden') {
        service.stopTracking('background');
      }
    };

    const handleBridgeEvent = (eventName: string) => {
      if (eventName === BridgeEvent.SCREEN_UNLOCK) {
        service.startTracking(partnerId, searchParams);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    addBridgeEventListener(handleBridgeEvent);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      removeBridgeEventListener(handleBridgeEvent);
      service.stopTracking('unmount');
    };
  }, [location.search]);

  return serviceRef.current;
}
